import React, { useState, useEffect } from 'react';
import { Modal, Button, message, Spin, Typography, Tabs, Select } from 'antd';
import { PlayCircleOutlined, CodeOutlined, CheckOutlined, BgColorsOutlined } from '@ant-design/icons';
import MonacoEditor from '@monaco-editor/react';
import { postRequestSimulator } from '../../../../../utils/api/requests/request';
import { useTheme } from '../../../../../store/context/theme-context';
import { getEditorThemePreference, saveEditorThemePreference, MONACO_THEMES } from '../../../../../utils/helper/editorThemePreference';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const CodeEditorSimulatorModal = ({ 
  visible, 
  onCancel, 
  fieldName,
  fieldLabel,
  initialCode, 
  initialContext,
  onApplyChanges,
  language = 'javascript',
  enableTest = true,
  testCommand = 'ExeFormula'
}) => {
  const { theme } = useTheme();
  const [editorTheme, setEditorTheme] = useState(getEditorThemePreference());
  const [code, setCode] = useState(
    initialCode || '// Enter your code here\nreturn context;'
  );
  const [context, setContext] = useState(
    initialContext || '{\n  "customerId": "12345",\n  "requestId": "REQ-001",\n  "amount": 5000,\n  "customerName": "John Doe"\n}'
  );
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('code');
  const [hasChanges, setHasChanges] = useState(false);

  // Update code when initialCode prop changes
  useEffect(() => {
    if (visible && initialCode !== undefined) {
      setCode(initialCode);
      setHasChanges(false);
    }
    if (visible && initialContext !== undefined) {
      const contextStr = typeof initialContext === 'string' 
        ? initialContext 
        : JSON.stringify(initialContext, null, 2);
      setContext(contextStr);
    }
    // Load saved editor theme preference when modal opens
    if (visible) {
      setEditorTheme(getEditorThemePreference());
    }
  }, [visible, initialCode, initialContext]);

  const handleThemeChange = (newTheme) => {
    setEditorTheme(newTheme);
    saveEditorThemePreference(newTheme);
    message.success('Editor theme preference saved!');
  };

  const handleCodeChange = (value) => {
    setCode(value || '');
    setHasChanges(true);
  };

  const handleApplyChanges = () => {
    if (onApplyChanges && hasChanges) {
      onApplyChanges(code);
      message.success('Changes applied successfully!');
      setHasChanges(false);
    }
  };

  const handleRunSimulation = async () => {
    if (!enableTest) {
      message.warning('Testing is not enabled for this field');
      return;
    }

    setIsRunning(true);
    setOutput('');
    setActiveTab('output'); // Auto-switch to output tab

    try {
      // Parse context JSON
      let contextObj;
      try {
        contextObj = JSON.parse(context);
      } catch (err) {
        throw new Error('Invalid JSON in context: ' + err.message);
      }

      // Call the test command API
      const data = { 
        formula: code, 
        context: contextObj 
      };
      
      const response = await postRequestSimulator(testCommand, data);

      if (response) {
        // Format output
        setOutput(JSON.stringify(response, null, 2));
        message.success('Execution completed successfully!');
      } else {
        throw new Error('No response from server');
      }
    } catch (error) {
      const errorOutput = {
        success: false,
        error: error.message || 'Execution failed',
        details: error?.data || error.toString(),
        field: fieldName || 'Unknown'
      };

      setOutput(JSON.stringify(errorOutput, null, 2));
      message.error('Execution failed: ' + (error.message || 'Unknown error'));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '40px' }}>
          <span>
            <CodeOutlined style={{ marginRight: 8 }} />
            Code Editor - {fieldLabel || fieldName || 'Script'}
          </span>
          <Select
            value={editorTheme}
            onChange={handleThemeChange}
            style={{ width: 180, marginLeft: 16 }}
            size="small"
            suffixIcon={<BgColorsOutlined />}
            options={MONACO_THEMES}
          />
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
        <Button
          key="apply"
          type="default"
          icon={<CheckOutlined />}
          onClick={handleApplyChanges}
          disabled={!hasChanges}
        >
          Apply Changes
        </Button>,
        enableTest && (
          <Button
            key="run"
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={handleRunSimulation}
            loading={isRunning}
          >
            Run Test
          </Button>
        )
      ].filter(Boolean)}
      bodyStyle={{ padding: 0, height: '70vh' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ height: '100%' }}
        tabBarStyle={{ padding: '0 16px', margin: 0 }}
      >
        {/* Tab 1: Code/Script */}
        <TabPane tab={language === 'json' ? 'JSON Editor' : 'Code Editor'} key="code">
          <div style={{ height: '60vh', padding: '16px' }}>
            <Text strong>{fieldLabel || fieldName || 'Code'}</Text>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              {language === 'json' 
                ? 'Edit your JSON configuration. Syntax errors will be highlighted.' 
                : 'Edit your script code. Use Ctrl+Space for auto-completion.'}
            </Paragraph>
            <MonacoEditor
              height="calc(100% - 50px)"
              language={language}
              value={code}
              onChange={handleCodeChange}
              theme={editorTheme}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                formatOnPaste: true,
                formatOnType: true,
                suggest: {
                  snippetsPreventQuickSuggestions: false
                },
                tabSize: 2,
                wordWrap: 'on'
              }}
            />
          </div>
        </TabPane>

        {/* Tab 2: Context (Input) */}
        <TabPane tab="Context (Input)" key="context">
          <div style={{ height: '60vh', padding: '16px' }}>
            <Text strong>Test Context (JSON)</Text>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              Provide test data for execution. This will be available as the <code>context</code> object during execution.
            </Paragraph>
            <MonacoEditor
              height="calc(100% - 50px)"
              language="json"
              value={context}
              onChange={(value) => setContext(value || '')}
              theme={editorTheme}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                formatOnPaste: true,
                formatOnType: true,
                tabSize: 2
              }}
            />
          </div>
        </TabPane>

        {/* Tab 3: Output (Result) */}
        <TabPane 
          tab={
            <span>
              Output {isRunning && <Spin size="small" style={{ marginLeft: 8 }} />}
            </span>
          } 
          key="output"
        >
          <div style={{ height: '60vh', padding: '16px' }}>
            <Text strong>Execution Output</Text>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              {output ? 'Result of code execution' : enableTest ? 'Click "Run Test" to execute the code' : 'Test execution not available for this field'}
            </Paragraph>
            <MonacoEditor
              height="calc(100% - 50px)"
              language="json"
              value={output || (enableTest ? '// No output yet. Click "Run Test" to execute the code.' : '// Test execution not available')}
              theme={editorTheme}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2
              }}
            />
          </div>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default CodeEditorSimulatorModal;
